import {
  BIG_JUMP,
  LOW_RES_MODEL,
  cappedBy,
  enlargedBy,
  isRefine,
  largestJump,
  type Dimensions,
  type UpscaleOptions,
} from '../services/upscaleService'
import './upscale.css'

/**
 * The Upscale settings
 * -------------------------------------------------------------
 * One set of controls for the three places Upscale is used -- the page, the
 * card on a board and the Miro panel -- so a model, a rule about sizes or a
 * hint is added once and is the same everywhere.
 *
 * Every model is a row with its kind beside its name, one word: Fidelity
 * keeps the picture as it is, Enhance rebuilds lost detail (Huanrui,
 * 2026-10-05). The size a picture already has decides which sizes are worth
 * offering, and a jump from a very small picture suggests the model made for
 * small pictures.
 */

export interface UpscaleChoice {
  model: string
  size: string
  face: boolean
}

interface UpscaleControlsProps {
  options: UpscaleOptions
  value: UpscaleChoice
  /** The pictures it would be run on, where their size is known. */
  pictures: Dimensions[]
  onChange: (patch: Partial<UpscaleChoice>) => void
  /** Narrow: the board's card and the Miro panel. Each model's line shows only when chosen. */
  compact?: boolean
  /**
   * The model list takes whatever height is left, each model growing equally,
   * every one with its description (Huanrui's layouts, 2026-10-05). For a
   * column that should fill its height: the Upscale page and the Miro panel.
   */
  fill?: boolean
  /** Names only, no descriptions: the Miro panel, where they did not fit (2026-10-13). */
  namesOnly?: boolean
  disabled?: boolean
}

/**
 * The size to use: the one asked for, or the first that would enlarge
 * something. Refine only with a model that offers it (Wonder 3.5).
 */
export function usableSize(options: UpscaleOptions, size: string, pictures: Dimensions[], model?: string): string {
  const offered = options.models.find((one) => one.key === model)?.refine === true
  const sizes = model === undefined || offered ? options.sizes : options.sizes.filter((one) => !isRefine(one))
  const asked = sizes.find((one) => one.value === size)
  if (pictures.length === 0 || (asked && enlargedBy(asked, pictures) > 0)) return asked?.value ?? sizes[0]?.value ?? ''
  return sizes.find((one) => enlargedBy(one, pictures) > 0)?.value ?? asked?.value ?? sizes[0]?.value ?? ''
}

export function UpscaleControls({ options, value, pictures, onChange, compact, fill, namesOnly, disabled }: UpscaleControlsProps) {
  const model = options.models.find((one) => one.key === value.model) ?? options.models[0]
  // Refine with a model that does not offer it: the first size that does.
  const size =
    options.sizes.find((one) => one.value === value.size && (!isRefine(one) || model?.refine)) ??
    options.sizes.find((one) => !isRefine(one))
  if (!model || !size) return null

  const known = pictures.length
  const enlarged = enlargedBy(size, pictures)
  const skipped = known - enlarged
  const jump = largestJump(size, pictures)
  const capped = cappedBy(model, size, pictures)

  return (
    <div className={`wwup${compact ? ' wwup--compact' : ''}${fill && !namesOnly ? ' wwup--fill' : ''}${namesOnly ? ' wwup--names' : ''}`}>
      <div className="wwup__field wwup__field--models">
        <span className="wwup__label">Model</span>
        <div className="wwup__models" role="radiogroup" aria-label="Model">
          {options.models.map((one) => {
            const on = one.key === model.key
            return (
              <button
                key={one.key}
                type="button"
                role="radio"
                aria-checked={on}
                className={`wwup__model nodrag${on ? ' wwup__model--on' : ''}`}
                disabled={disabled}
                onClick={() =>
                  onChange(
                    // Away from Wonder 3.5 on Refine: the size goes to one the model makes.
                    !one.refine && isRefine(size)
                      ? { model: one.key, size: options.sizes.find((other) => !isRefine(other))?.value ?? '2k' }
                      : { model: one.key },
                  )
                }
                title={one.blurb}
              >
                <span className="wwup__model-top">
                  <span className="wwup__model-name">{one.label}</span>
                  <span className={`wwup__kind wwup__kind--${one.kind.toLowerCase()}`}>{one.kind}</span>
                </span>
                {!namesOnly && (!compact || fill || on) ? <span className="wwup__blurb">{one.blurb}</span> : null}
              </button>
            )
          })}
        </div>
      </div>

      <div className="wwup__field">
        <span className="wwup__label">Size</span>
        <div className="wwup__chips">
          {options.sizes.map((one) => {
            const none = known > 0 && enlargedBy(one, pictures) === 0
            const refine = isRefine(one)
            if (refine && !model.refine) return null
            return (
              <button
                key={one.value}
                type="button"
                className={`wwup__chip nodrag${one.value === size.value ? ' wwup__chip--on' : ''}`}
                disabled={disabled || none}
                title={
                  refine
                    ? 'The same size, its detail rebuilt -- for a picture already 4K, as many times as you like'
                    : none
                    ? known === 1
                      ? `This picture is already ${one.label} or larger`
                      : `These pictures are already ${one.label} or larger`
                    : `${one.longEdge} pixels on the longer side`
                }
                onClick={() => onChange({ size: one.value })}
              >
                {one.label}
                <span className="wwup__chip-sub">{refine ? 'Same size' : one.longEdge}</span>
              </button>
            )
          })}
        </div>
        {capped > 0 ? (
          <p className="wwup__note">
            {model.label} enlarges at most {model.maxFactor}×, so {capped === known ? (known === 1 ? 'this one' : 'these') : `${capped} of these`} will
            come out smaller than {size.label}. Low Resolution 2 goes further.
          </p>
        ) : null}
        {skipped > 0 && enlarged > 0 ? (
          <p className="wwup__note">
            {skipped} of {known} {skipped === 1 ? 'is' : 'are'} already {size.label} or larger and will be left out.
          </p>
        ) : null}
      </div>

      {/* Only for a model that has it (Huanrui, 2026-10-16): Wonder 3.5 and
          H1 handle faces themselves, and a switch that cannot be used is
          only something to read. */}
      {model.face ? (
      <div className="wwup__field">
        <label className={`wwup__switch nodrag${model.face ? '' : ' wwup__switch--off'}`}>
          <input
            type="checkbox"
            checked={model.face && value.face}
            disabled={disabled || !model.face}
            onChange={(event) => onChange({ face: event.target.checked })}
          />
          <span className="wwup__track" aria-hidden="true" />
          <span className="wwup__switch-text">
            Improve faces
            <span className="wwup__switch-sub">
              {model.face ? 'Reshapes face detail. Best on people in AI-made pictures.' : `${model.label} handles faces itself.`}
            </span>
          </span>
        </label>
      </div>
      ) : null}

      {jump > BIG_JUMP && model.key !== LOW_RES_MODEL && capped === 0 ? (
        <p className="wwup__hint">
          A big jump — {Math.round(jump)} times larger.{' '}
          {options.models.some((one) => one.key === LOW_RES_MODEL) ? (
            <button
              type="button"
              className="wwup__link nodrag"
              disabled={disabled}
              onClick={() => onChange({ model: LOW_RES_MODEL })}
            >
              Low Resolution 2
            </button>
          ) : (
            'Low Resolution 2'
          )}{' '}
          suits small pictures.
        </p>
      ) : null}
    </div>
  )
}
